import { create } from 'zustand';
import api from '../lib/api';
import { readPersistedOrgTypes, writePersistedOrgTypes, ORG_TYPE_FILTER_OPTIONS } from '../lib/orgTypes';
import type {
  OrganizationListItem,
  OrganizationWithPeople,
  OrganizationSearchResult,
  OrganizationFilters,
  OrganizationUpdate,
  OrganizationCounts,
  OrganizationsSegmentCounts,
  PaginationMeta,
  CustomerStatus,
} from '../types';

interface OrganizationState {
  // List state
  organizations: OrganizationListItem[];
  pagination: PaginationMeta | null;
  filters: OrganizationFilters;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;

  // Segment counts and active segment
  counts: OrganizationCounts | null;
  segment: string | null;

  // In-scope segment counts (from /api/organizations/segment-counts)
  segmentCounts: OrganizationsSegmentCounts | null;
  isLoadingCounts: boolean;

  // Available states (for filtering dropdown — states that return rows under the current filters, excluding the state filter itself (CRM-158))
  availableStates: string[];

  // Detail state
  currentOrganization: OrganizationWithPeople | null;
  isLoadingDetail: boolean;
  detailError: string | null;

  // Search state
  searchResults: OrganizationSearchResult[];
  isSearching: boolean;

  // Actions - List
  fetchOrganizations: () => Promise<void>;
  fetchCounts: () => Promise<void>;
  fetchSegmentCounts: () => Promise<void>;
  setSegment: (segment: string | null) => void;
  setFilters: (filters: Partial<OrganizationFilters>) => void;
  toggleCustomerStatus: (state: CustomerStatus) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;

  // Actions - Detail
  fetchOrganization: (id: string, opts?: { includeFormer?: boolean }) => Promise<void>;
  updateOrganization: (id: string, data: OrganizationUpdate) => Promise<void>;
  clearCurrentOrganization: () => void;

  // Actions - Search
  searchOrganizations: (query: string, state?: string) => Promise<void>;
  clearSearchResults: () => void;

  // Actions - Utility
  clearError: () => void;
}

// Monotonically increasing id for the most recent searchOrganizations call —
// only the newest call is allowed to write searchResults/isSearching. Without
// this, a slower earlier keystroke's response can resolve after a faster
// later one and clobber the results with stale data (CRM-143).
let latestSearchRequestId = 0;

const DEFAULT_FILTERS: OrganizationFilters = {
  sortBy: 'name',
  sortOrder: 'asc',
  // CRM-16: list opens on "Mine" scope by default (orgs the rep owns via an
  // active NamedAccount, resolved server-side). One click to "All".
  // clearFilters() resets here, so clearing stays in Mine too.
  assignedTo: 'me',
  orgTypes: readPersistedOrgTypes(),
};

export const useOrganizationStore = create<OrganizationState>((set, get) => ({
  // Initial state - List
  organizations: [],
  pagination: null,
  filters: DEFAULT_FILTERS,
  currentPage: 1,
  pageSize: 25,
  isLoading: false,
  error: null,

  counts: null,
  segment: null,

  // Initial state - Segment counts
  segmentCounts: null,
  isLoadingCounts: false,

  availableStates: [],

  // Initial state - Detail
  currentOrganization: null,
  isLoadingDetail: false,
  detailError: null,

  // Initial state - Search
  searchResults: [],
  isSearching: false,

  // ==================== LIST ACTIONS ====================

  /**
   * Fetch organizations with current filters and pagination
   */
  fetchOrganizations: async () => {
    const { filters, currentPage, pageSize } = get();

    try {
      set({ isLoading: true, error: null });

      // Build query params
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (filters.search) params.append('search', filters.search);
      if (filters.state) params.append('state', filters.state);
      if (filters.city) params.append('city', filters.city);
      if (filters.isPrivate !== undefined) params.append('isPrivate', filters.isPrivate.toString());
      if (filters.ncesLevel) params.append('ncesLevel', filters.ncesLevel);
      if (filters.hasBlastLink !== undefined) params.append('hasBlastLink', filters.hasBlastLink.toString());
      if (filters.segment) params.append('segment', filters.segment);
      if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);
      if (filters.customerStatus && filters.customerStatus.length > 0) {
        params.append('customerStatus', filters.customerStatus.join(','));
      }
      if (filters.orgTypes && filters.orgTypes.length > 0) {
        const expanded = filters.orgTypes.flatMap(
          (v) => ORG_TYPE_FILTER_OPTIONS.find((o) => o.value === v)?.matchValues ?? [v],
        );
        params.append('orgTypes', Array.from(new Set(expanded)).join(','));
      }
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

      const response = await api.get(`/api/organizations?${params.toString()}`);

      set({
        organizations: response.data.organizations,
        pagination: response.data.pagination,
        availableStates: response.data.availableStates || [],
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch organizations:', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to load organizations',
      });
    }
  },

  /**
   * Fetch organization segment counts
   */
  fetchCounts: async () => {
    try {
      const response = await api.get('/api/organizations/counts');
      set({ counts: response.data });
    } catch (error) {
      console.error('Failed to fetch org counts:', error);
    }
  },

  /**
   * Fetch in-scope segment counts (from /api/organizations/segment-counts).
   * Scoped to the current assignedTo filter.
   */
  fetchSegmentCounts: async () => {
    const { filters } = get();
    set({ isLoadingCounts: true });
    try {
      const params = new URLSearchParams();
      if (filters.assignedTo) params.append('assignedTo', filters.assignedTo);

      const res = await api.get<OrganizationsSegmentCounts>(
        `/api/organizations/segment-counts?${params.toString()}`,
      );
      set({ segmentCounts: res.data, isLoadingCounts: false });
    } catch (error: unknown) {
      set({ isLoadingCounts: false });
      console.error('Failed to fetch segment counts:', error);
    }
  },

  /**
   * Set active segment and update filters
   */
  setSegment: (segment) => {
    set({ segment });
    get().setFilters({ segment: segment || undefined });
  },

  /**
   * Update filters and reset to page 1.
   * When assignedTo (scope) changes, also re-fetch segment counts.
   */
  setFilters: (newFilters: Partial<OrganizationFilters>) => {
    if (newFilters.orgTypes !== undefined) {
      writePersistedOrgTypes(newFilters.orgTypes);
    }
    const prevAssigned = get().filters.assignedTo;
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      currentPage: 1, // Reset to first page when filters change
    }));
    // Auto-fetch with new filters
    get().fetchOrganizations();
    // Re-fetch segment counts only when the scope (assignedTo) actually changed
    if (
      'assignedTo' in newFilters &&
      newFilters.assignedTo !== prevAssigned
    ) {
      get().fetchSegmentCounts();
    }
  },

  /**
   * Toggle a CustomerStatus value in the multi-select filter
   */
  toggleCustomerStatus: (state: CustomerStatus) => {
    const current = get().filters.customerStatus ?? [];
    const next = current.includes(state)
      ? current.filter((s) => s !== state)
      : [...current, state];
    set((s) => ({ filters: { ...s.filters, customerStatus: next.length ? next : undefined } }));
    get().fetchOrganizations();
  },

  /**
   * Clear all filters
   */
  clearFilters: () => {
    // CRM-72: Type is its own always-visible axis with its own Clear control
    // (spec §9.3) — the Advanced "Clear filters" leaves the remembered Type
    // selection alone, rather than resetting it to the module-init
    // DEFAULT_FILTERS snapshot. Persistence is untouched.
    set((state) => ({
      filters: { ...DEFAULT_FILTERS, orgTypes: state.filters.orgTypes },
      currentPage: 1,
    }));
    get().fetchOrganizations();
  },

  /**
   * Set current page
   */
  setPage: (page: number) => {
    set({ currentPage: page });
    get().fetchOrganizations();
  },

  /**
   * Set page size
   */
  setPageSize: (size: number) => {
    set({ pageSize: size, currentPage: 1 });
    get().fetchOrganizations();
  },

  // ==================== DETAIL ACTIONS ====================

  /**
   * Fetch single organization by ID
   */
  fetchOrganization: async (id: string, opts?: { includeFormer?: boolean }) => {
    try {
      set({ isLoadingDetail: true, detailError: null });

      const qs = opts?.includeFormer ? '?includeFormer=true' : '';
      const response = await api.get(`/api/organizations/${id}${qs}`);

      set({
        currentOrganization: response.data.organization,
        isLoadingDetail: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch organization:', error);
      set({
        isLoadingDetail: false,
        detailError: error.response?.data?.message || 'Failed to load organization',
      });
    }
  },

  /**
   * Update organization CRM fields
   */
  updateOrganization: async (id: string, data: OrganizationUpdate) => {
    try {
      set({ isLoadingDetail: true, detailError: null });

      const response = await api.patch(`/api/organizations/${id}`, data);

      // Update current organization if it's the same one
      const { currentOrganization } = get();
      if (currentOrganization?.id === id) {
        set({
          currentOrganization: {
            ...currentOrganization,
            ...response.data.organization,
          },
        });
      }

      set({ isLoadingDetail: false });

      // Refresh list to reflect changes
      get().fetchOrganizations();
    } catch (error: any) {
      console.error('Failed to update organization:', error);
      set({
        isLoadingDetail: false,
        detailError: error.response?.data?.message || 'Failed to update organization',
      });
      throw error;
    }
  },

  /**
   * Clear current organization (when navigating away)
   */
  clearCurrentOrganization: () => {
    set({ currentOrganization: null, detailError: null });
  },

  // ==================== SEARCH ACTIONS ====================

  /**
   * Search organizations (typeahead)
   */
  searchOrganizations: async (query: string, state?: string) => {
    if (query.length < 2) {
      latestSearchRequestId += 1; // any older in-flight request is now stale
      set({ searchResults: [] });
      return;
    }

    const requestId = ++latestSearchRequestId;
    try {
      set({ isSearching: true });

      const params = new URLSearchParams();
      params.append('q', query);
      if (state) params.append('state', state);
      params.append('limit', '30'); // common school names tie at the top — 10 slots hid them (CRM-150 Addendum A)

      const response = await api.get(`/api/organizations/search?${params.toString()}`);

      if (requestId !== latestSearchRequestId) return; // a newer search raced ahead
      set({
        searchResults: response.data.organizations,
        isSearching: false,
      });
    } catch (error: any) {
      if (requestId !== latestSearchRequestId) return;
      console.error('Failed to search organizations:', error);
      set({ isSearching: false, searchResults: [] });
    }
  },

  /**
   * Clear search results
   */
  clearSearchResults: () => {
    latestSearchRequestId += 1; // any in-flight request is now stale too
    set({ searchResults: [] });
  },

  // ==================== UTILITY ACTIONS ====================

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null, detailError: null });
  },
}));
